import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  TeamOutlined,
  UserOutlined,
  SolutionOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useBatches, batchLabel } from '../hooks/useBatches';
import {
  adminPageWrap,
  adminAlertStyle,
  adminFilterCardStyle,
  adminContentCardStyle,
} from '../styles/adminPageShell';

/**
 * 「提名领导指定」管理员页面（V1.3）。
 *
 * 业务流程：
 *   HR 在此页录入「部门 + 提名领导 + 提名人数」→ 领导登录后即可在 `/nominator`
 *   看到本批次自己的部门额度并按提名格式提交。线下通知由 HR 自行安排，
 *   系统不再走任何推送渠道。
 *
 * 关键设计：
 *   - 部门 / 提名领导 全部「自由文本」录入，不强依赖组织树和花名册 ——
 *     业务上常见跨部门、文本部门别名（"产品研发"≠"产品技术部"）等情况，文本最可靠。
 *   - 后端会在保存时尝试用精确姓名 / 部门名匹配回填外键，命中即下游可用，
 *     未命中保留文本，领导端用 leaderId / leaderName 双键定位均可。
 *   - quota（名额）按"领导维度"写入 batch.nominationLeaderCaps[leaderId]，
 *     同一领导跨部门共用一个上限（更贴合"一个人最多提多少"的业务直觉）。
 */

type Assignment = {
  id: number;
  batchId: number;
  deptId: number | null;
  deptName: string | null;
  leaderId: number | null;
  leaderName: string | null;
  quota: number | null;
  effectiveAt: string;
  revokedAt: string | null;
};

const { Text } = Typography;

export function NominationLeadersPage() {
  const qc = useQueryClient();
  const { batches, batchId, setBatchId } = useBatches();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const { data: assignments = [] } = useQuery<Assignment[]>({
    queryKey: ['nomination-leaders', batchId],
    queryFn: () => api.get('/admin/nomination-leaders', { params: { batchId } }),
    enabled: !!batchId,
  });

  const stats = useMemo(() => {
    const leaderIds = new Set(
      assignments.filter((a) => a.leaderId != null).map((a) => a.leaderId),
    );
    const leaderNames = new Set(
      assignments.map((a) => (a.leaderName ?? '').trim()).filter(Boolean),
    );
    const totalQuota = assignments.reduce(
      (s, a) => s + (a.quota ?? 0),
      0,
    );
    return {
      assignmentCount: assignments.length,
      leaderCount: leaderIds.size || leaderNames.size,
      totalQuota,
    };
  }, [assignments]);

  const submitMutation = useMutation({
    mutationFn: (vals: {
      deptName: string;
      leaderName: string;
      quota?: number;
    }) =>
      editingId
        ? api.patch(`/admin/nomination-leaders/${editingId}`, vals)
        : api.post('/admin/nomination-leaders', { batchId, ...vals }),
    onSuccess: () => {
      message.success(editingId ? '已更新' : '已指定');
      setModalOpen(false);
      setEditingId(null);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['nomination-leaders'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? '保存失败'),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/nomination-leaders/${id}`),
    onSuccess: () => {
      message.success('已撤销');
      qc.invalidateQueries({ queryKey: ['nomination-leaders'] });
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? '撤销失败'),
  });

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row: Assignment) => {
    setEditingId(row.id);
    form.setFieldsValue({
      deptName: row.deptName ?? '',
      leaderName: row.leaderName ?? '',
      quota: row.quota ?? undefined,
    });
    setModalOpen(true);
  };

  return (
    <div style={adminPageWrap}>
      <div className="tg-page-title">提名领导指定</div>

      <Alert
        type="info"
        showIcon
        style={adminAlertStyle}
        message="维护「提名领导 + 部门 + 名额」名单"
        description={
          <Text type="secondary">
            指派完成后，对应领导登录即可在「晋升提名」页看到自己的部门额度，按
            「提名人 / 晋升职级 / 晋升理由」三项填写。通知方式由 HR 线下安排，高潜识别是另一份独立名单。
          </Text>
        }
      />

      <Card style={{ ...adminFilterCardStyle, marginBottom: 16 }} styles={{ body: { paddingBottom: 12 } }}>
        <Space wrap size={12} style={{ marginBottom: 12 }}>
          <Select
            placeholder="选择批次"
            style={{ width: 220 }}
            value={batchId}
            options={batches.map((b) => ({ label: batchLabel(b), value: b.id }))}
            onChange={setBatchId}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!batchId}
            onClick={openCreate}
          >
            新增指派
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            修改即新指派一行（旧行作废留痕）；撤销 = 软删除，已提交的提名仍保留。
          </Text>
        </Space>

        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="本批次指派条数"
              value={stats.assignmentCount}
              prefix={<SolutionOutlined style={{ color: '#722ed1' }} />}
              suffix="行"
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="覆盖领导人数"
              value={stats.leaderCount}
              prefix={<UserOutlined style={{ color: '#1677ff' }} />}
              suffix="人"
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="名额合计"
              value={stats.totalQuota}
              prefix={<TeamOutlined style={{ color: '#52c41a' }} />}
              suffix={stats.totalQuota > 0 ? '人' : ''}
            />
          </Col>
        </Row>
      </Card>

      <Card style={adminContentCardStyle} styles={{ body: { paddingTop: 12, paddingBottom: 12 } }}>
        <Table<Assignment>
          rowKey="id"
          dataSource={assignments}
          size="middle"
          pagination={{ pageSize: 20, showTotal: (n) => `共 ${n} 行` }}
          columns={[
            {
              title: '提名领导',
              key: 'leader',
              render: (_, r) => (
                <Space size={6}>
                  <span style={{ fontWeight: 500 }}>{r.leaderName ?? '-'}</span>
                  {r.leaderId == null && r.leaderName ? (
                    <Tag color="warning">未匹配花名册</Tag>
                  ) : null}
                </Space>
              ),
            },
            {
              title: '提名部门',
              key: 'dept',
              render: (_, r) =>
                r.deptName ? (
                  <span>{r.deptName}</span>
                ) : (
                  <Text type="secondary">（旧数据，仅有 deptId={r.deptId}）</Text>
                ),
            },
            {
              title: '名额',
              key: 'quota',
              width: 100,
              align: 'center',
              render: (_, r) =>
                r.quota == null ? (
                  <Text type="secondary">不限</Text>
                ) : (
                  <span style={{ fontWeight: 600, color: '#1677ff' }}>{r.quota}</span>
                ),
            },
            {
              title: '操作',
              key: 'op',
              width: 150,
              align: 'center',
              render: (_, r) => (
                <Space size={4}>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEdit(r)}
                  >
                    编辑
                  </Button>
                  <Popconfirm
                    title="撤销该指派？"
                    description="撤销后该领导失去本部门的提名权（已提交的提名仍保留）"
                    onConfirm={() => revokeMutation.mutate(r.id)}
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    >
                      撤销
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editingId ? '编辑指派' : '新增提名指派'}
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalOpen(false);
          setEditingId(null);
          form.resetFields();
        }}
        confirmLoading={submitMutation.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) =>
            submitMutation.mutate({
              deptName: (v.deptName ?? '').trim(),
              leaderName: (v.leaderName ?? '').trim(),
              quota: v.quota,
            })
          }
        >
          <Form.Item
            label="提名领导"
            name="leaderName"
            rules={[{ required: true, message: '请填写领导姓名' }]}
            extra="填花名册中的姓名，后端会精确匹配；未匹配上时该指派仍生效，领导登录后照常可见。"
          >
            <Input placeholder="例如：郭天山" />
          </Form.Item>
          <Form.Item
            label="提名部门"
            name="deptName"
            rules={[{ required: true, message: '请填写部门名称' }]}
            extra="按花名册的部门口径填，例如「设计部」「产品研发部」。同一批次同一部门可指派多名领导，不限数量。"
          >
            <Input placeholder="例如：设计部" />
          </Form.Item>
          <Form.Item
            label="名额"
            name="quota"
            extra="该领导本批次可提交的最大提名人数；留空 = 不限"
          >
            <InputNumber
              min={0}
              max={50}
              step={1}
              precision={0}
              controls={false}
              style={{ width: '100%' }}
              placeholder="例如：3"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
